import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  AuthTokens,
  LoginInput,
  SignupInput,
  MyOrganization,
} from "../types";

const AUTH_FIELDS = `
  accessToken
  refreshToken
  sessionToken
  role
  userId
  organizationId
  email
  name
  organizationStatus
`;

// Canonical auth / token API. `graphqlBaseQuery` already unwraps the GraphQL
// envelope to `data`, so `transformResponse` receives `{ login: ... }` etc.
export const tokenApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<AuthTokens, LoginInput>({
      query: (input) => ({
        body: {
          query: `
            mutation Login($input: LoginInput!) {
              login(input: $input) {
                accessToken
                refreshToken
                sessionToken
                role
                userId
                organizationId
                email
                name
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<AuthTokens>(res, "login"),

      invalidatesTags: ["Auth"],
    }),

    signup: builder.mutation<AuthTokens, SignupInput>({
      query: (input) => ({
        body: {
          query: `
            mutation Signup($input: SignupInput!) {
              signup(input: $input) {
                ${AUTH_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<AuthTokens>(res, "signup"),

      invalidatesTags: ["Auth"],
    }),

    refreshAccessToken: builder.query<
      { accessToken: string; refreshToken: string },
      string
    >({
      query: (refreshToken) => ({
        body: {
          query: `
            query RefreshAccessToken($refreshToken: String!) {
              refreshAccessToken(refreshToken: $refreshToken) {
                accessToken
                refreshToken
              }
            }
          `,
          variables: { refreshToken },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<{ accessToken: string; refreshToken: string }>(
          res,
          "refreshAccessToken",
        ),
    }),

    logout: builder.mutation<boolean, string>({
      query: (sessionId) => ({
        body: {
          query: `
            mutation Logout($sessionId: String!) {
              logout(sessionId: $sessionId)
            }
          `,
          variables: { sessionId },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "logout"),

      invalidatesTags: ["Auth"],
    }),

    // Revokes every active session for the caller, across every device.
    logoutAllDevices: builder.mutation<boolean, void>({
      query: () => ({
        body: {
          query: `
            mutation LogoutAllDevices {
              logoutAllDevices
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "logoutAllDevices"),

      invalidatesTags: ["Auth"],
    }),

    // Organizations the signed-in user belongs to (for the picker / switcher).
    myOrganizations: builder.query<MyOrganization[], void>({
      query: () => ({
        body: {
          query: `
            query MyOrganizations {
              myOrganizations {
                id
                name
                role
                status
                organizationStatus
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) =>
        pick<MyOrganization[]>(res, "myOrganizations"),

      providesTags: ["Auth"],
    }),

    // Re-issue tokens scoped to another organization the caller belongs to.
    switchOrganization: builder.mutation<AuthTokens, string>({
      query: (organizationId) => ({
        body: {
          query: `
            mutation SwitchOrganization($organizationId: String!) {
              switchOrganization(organizationId: $organizationId) {
                ${AUTH_FIELDS}
              }
            }
          `,
          variables: { organizationId },
        },
      }),

      transformResponse: (res: unknown) =>
        pick<AuthTokens>(res, "switchOrganization"),
    }),
  }),
});

export const {
  useLoginMutation,
  useSignupMutation,
  useRefreshAccessTokenQuery,
  useLazyRefreshAccessTokenQuery,
  useLogoutMutation,
  useLogoutAllDevicesMutation,
  useMyOrganizationsQuery,
  useSwitchOrganizationMutation,
} = tokenApi;
